/** @jsxImportSource preact */
import { ComponentChildren } from 'preact';
import { Modal } from './Modal';
import type { IncidentDetail } from './IncidentDetailModal';
import { NatSecRadar } from './NatSecRadar';
import { IMPACT_PALETTE, IMPACT_LABELS, NATSEC_FRAMEWORK_URL, type ImpactPillVariant } from '../data/impactScale';
import { TEXT_LINK_COLOR } from '../lib/theme';

interface NatSecIncidentDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  incidentId: string;
  incident: IncidentDetail | null;
  loading: boolean;
}

function readString(doc: IncidentDetail | null, key: string): string {
  if (!doc) return '';
  const v = doc[key];
  if (v === undefined || v === null) return '';
  const s = String(v).trim();
  return s;
}

function readNumber(doc: IncidentDetail | null, key: string): number | null {
  if (!doc) return null;
  const v = doc[key];
  if (v === undefined || v === null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Airtable Attachment fields come through as an array of `{ url, filename, ... }`
 * objects. The Spider chart image is one such field; extract the first URL.
 */
function readAttachmentUrl(doc: IncidentDetail | null, key: string): string {
  if (!doc) return '';
  const v = doc[key];
  if (!Array.isArray(v) || v.length === 0) return '';
  const first = v[0];
  if (first && typeof first === 'object' && 'url' in first && typeof (first as { url: unknown }).url === 'string') {
    return (first as { url: string }).url;
  }
  return '';
}

// The canonical 1–5 colour palette + words live in src/data/impactScale.ts
// (shared with the radars and their scale key). The variant type is
// re-exported for callers that import it from here.
export type { ImpactPillVariant };

const PALETTE_BY_SCORE = new Map(IMPACT_PALETTE.map((l) => [l.score, l]));

function resolveLevel(variant: ImpactPillVariant, score?: number | null, label?: string) {
  if (score != null) {
    const paletteEntry = PALETTE_BY_SCORE.get(score);
    if (paletteEntry) return { score, label: IMPACT_LABELS[variant][score], bg: paletteEntry.bg };
  }
  if (label) {
    const needle = label.toLowerCase();
    for (const [s, l] of Object.entries(IMPACT_LABELS[variant])) {
      if (l.toLowerCase() === needle) {
        const paletteEntry = PALETTE_BY_SCORE.get(Number(s));
        if (paletteEntry) return { score: Number(s), label: l, bg: paletteEntry.bg };
      }
    }
  }
  return undefined;
}

function SectionHeading({ children }: { children: ComponentChildren }) {
  return (
    <h3 style={{ margin: '0 0 12px 0', fontSize: 15, fontWeight: 600, color: 'var(--gc-foreground, inherit)' }}>
      {children}
    </h3>
  );
}

function SectionDivider() {
  return <div style={{ borderTop: '2px solid var(--gc-border, #e5e7eb)', margin: '8px 0 0 0' }} />;
}

function LinkButton({ href, label, title }: { href: string; label: string; title?: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      title={title}
      style={{
        color: 'var(--gc-link, inherit)',
        textDecoration: 'none',
        fontWeight: 600,
        fontSize: 13,
        padding: '6px 14px',
        border: '1px solid currentColor',
        borderRadius: 4,
        display: 'inline-block',
      }}
    >
      {label} ↗
    </a>
  );
}

/**
 * Colour-coded score pill showing the level's name (e.g. "Substantial"). Used for
 * NatSec impact scores AND the standard harm severity scores. The two
 * scales share colours but differ at 5: NatSec=Critical, standard=Catastrophic
 * — selected via the `variant` prop (default 'natsec').
 */
export function ImpactPill({ score, label, size = 'sm', variant = 'natsec' }: {
  score?: number | null;
  label?: string;
  size?: 'sm' | 'lg' | 'xs';
  variant?: ImpactPillVariant;
}) {
  const level = resolveLevel(variant, score, label);

  if (!level) {
    return <span style={{ color: 'var(--gc-text-muted, #9ca3af)' }}>—</span>;
  }

  const fontSize = size === 'lg' ? 14 : size === 'xs' ? 11 : 12;
  const padY = size === 'lg' ? 5 : size === 'xs' ? 2 : 3;
  const padX = size === 'lg' ? 12 : size === 'xs' ? 8 : 10;

  // Word only — the leading 1-5 score is dropped from the pill. The colour
  // already encodes the level, and the bare word reads faster in a dense
  // table. Sorting is unaffected: the table sorts on the numeric
  // highestSeverityScore / natSecOverallScore fields, never on this text.
  //
  // Every pill in a column renders at the same width — ragged pill edges
  // read as noise in a table. Rather than hard-code a width (which would
  // drift the moment a label changes, or under a different font), the
  // widest label for this variant is rendered as a zero-height, hidden
  // sizer stacked in the same grid cell. The grid takes the wider of the
  // two, so the pill is always exactly as wide as its longest sibling and
  // the visible text centres inside it.
  const widestLabel = Object.values(IMPACT_LABELS[variant])
    .reduce((a, b) => (b.length > a.length ? b : a), '');

  return (
    <span
      style={{
        display: 'inline-grid',
        justifyItems: 'center',
        padding: `${padY}px ${padX}px`,
        borderRadius: 999,
        background: level.bg,
        color: '#fff',
        fontSize,
        fontWeight: 700,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
      }}
    >
      <span aria-hidden="true" style={{ gridArea: '1 / 1', visibility: 'hidden', height: 0, overflow: 'hidden' }}>
        {widestLabel}
      </span>
      <span style={{ gridArea: '1 / 1' }}>
        {level.label}
      </span>
    </span>
  );
}

function LabelValue({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ padding: '8px 0', borderBottom: '1px solid var(--gc-border, #e5e7eb)' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gc-text-muted, #6b7280)', marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--gc-foreground, inherit)', minHeight: 20 }}>
        {value || '—'}
      </div>
    </div>
  );
}

/**
 * Generic categorical pill used for threat characteristics. Colour is
 * fixed per characteristic (imminence/autonomy/novelty) rather than
 * derived from the level value — see THREAT_PILL_COLOURS.
 */
function ColouredPill({ label, bg }: { label: string; bg: string }) {
  if (!label) return <span style={{ color: 'var(--gc-text-muted, #9ca3af)' }}>—</span>;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: 999,
        background: bg,
        color: '#fff',
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

/**
 * Two-column row used by both the Impact and Threat Characteristics
 * sections: label on the left, pill + justification stacked on the
 * right. Keeping the justification in the right column means it starts
 * directly under the level/score, not under the label.
 */
function PillRow({
  label,
  pill,
  justification,
}: {
  label: string;
  pill: ComponentChildren;
  justification: string;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '180px 1fr',
        gap: 16,
        padding: '12px 0',
        borderBottom: '1px solid var(--gc-border, #e5e7eb)',
        alignItems: 'start',
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--gc-foreground, inherit)' }}>
        {label}
      </div>
      <div>
        <div style={{ marginBottom: justification ? 6 : 0 }}>{pill}</div>
        {justification && (
          <div style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--gc-foreground, inherit)' }}>
            {justification}
          </div>
        )}
      </div>
    </div>
  );
}

// Pill colours for threat characteristics — fixed per dimension so the
// pill colour itself signals which characteristic is being assessed.
const THREAT_PILL_COLOURS = {
  imminence: '#0891b2',
  autonomy: '#d97706',
  novelty:  '#8b5cf6',
} as const;

const IMPACT_CATEGORIES: Array<{
  label: string;
  /** Radar axis label — the legacy spider image's wording. */
  shortLabel: string;
  scoreKey: string;
  justificationKey: string;
}> = [
  {
    label: 'Physical Security & Critical Infrastructure',
    shortLabel: 'Physical Security',
    scoreKey: 'natSecPhysicalScore',
    justificationKey: 'natSecPhysicalJustification',
  },
  {
    label: 'Information Warfare & Intelligence Security',
    shortLabel: 'Information Warfare',
    scoreKey: 'natSecInformationScore',
    justificationKey: 'natSecInformationJustification',
  },
  {
    label: 'Sovereignty & Government Functions',
    shortLabel: 'Sovereignty / Governance',
    scoreKey: 'natSecSovereigntyScore',
    justificationKey: 'natSecSovereigntyJustification',
  },
  {
    label: 'Economic & Technological Security',
    shortLabel: 'Economic Security',
    scoreKey: 'natSecEconomicScore',
    justificationKey: 'natSecEconomicJustification',
  },
  {
    label: 'Societal Stability & Human Rights',
    shortLabel: 'Societal Stability',
    scoreKey: 'natSecSocietalScore',
    justificationKey: 'natSecSocietalJustification',
  },
];

export interface NatSecDetailContentProps {
  incidentId: string;
  incident: IncidentDetail | null;
  /** Hide the AIID button and date line when the host page already shows them. */
  showLinks?: boolean;
}

/**
 * The National Security assessment body, without the modal shell or title.
 * Used by the NatSec modal and appended to the inline incident page.
 */
export function NatSecDetailContent({ incidentId, incident, showLinks = true }: NatSecDetailContentProps) {
  const date = readString(incident, 'date');
  const aiidUrl = incidentId ? `https://incidentdatabase.ai/cite/${encodeURIComponent(incidentId)}` : '';

  const summary = readString(incident, 'natSecSummary');
  const overallScore = readNumber(incident, 'natSecOverallScore');
  const scope = readString(incident, 'natSecScope');
  const primaryTarget = readString(incident, 'natSecPrimaryTarget');
  const otherAffected = readString(incident, 'natSecOtherAffected');
  const allegedPerpetrator = readString(incident, 'natSecAllegedPerpetrator');
  const spiderUrl = readAttachmentUrl(incident, 'natSecSpider');
  // Client-rendered radar replaces the pre-generated spider image when the
  // record has per-category scores but no attachment (Arcola datasets have
  // no images). Attachment present → image, exactly as before.
  const radarCategories = IMPACT_CATEGORIES.map((c) => ({
    label: c.shortLabel,
    score: readNumber(incident, c.scoreKey),
  }));
  const showRadar = !spiderUrl && radarCategories.some((c) => c.score != null);

  const imminenceLevel = readString(incident, 'natSecImminenceLevel');
  const imminenceJustification = readString(incident, 'natSecImminenceJustification');
  const autonomyLevel = readString(incident, 'natSecAutonomyLevel');
  const autonomyJustification = readString(incident, 'natSecAutonomyJustification');
  const noveltyLevel = readString(incident, 'natSecNoveltyLevel');
  const noveltyJustification = readString(incident, 'natSecNoveltyJustification');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Intro / framework attribution */}
      <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--gc-text-muted, #6b7280)' }}>
        <p style={{ margin: '0 0 6px' }}>
          The National Security Impact Assessment is based on{' '}
          <a
            href={NATSEC_FRAMEWORK_URL}
            target="_blank"
            rel="noreferrer"
            style={{ color: TEXT_LINK_COLOR, fontWeight: 600 }}
          >
            this framework
          </a>.
        </p>
        <p style={{ margin: 0 }}>
          The assessment uses voluntarily submitted incident reports and automated
          classification by an LLM. It should therefore be treated as indicative and
          all sources/analysis must be checked before being used or referenced.
        </p>
      </div>

      {/* AIID button + date inline (mirrors standard modal) */}
      {showLinks && (aiidUrl || date) && (
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          {aiidUrl && (
            <LinkButton
              href={aiidUrl}
              label="AI Incident Database"
              title="View full incident details and source reports on the AI Incident Database site"
            />
          )}
          {date && (
            <span style={{ fontSize: 13, color: 'var(--gc-text-muted, #6b7280)' }}>
              <strong style={{ color: 'var(--gc-foreground, inherit)' }}>Incident date:</strong> {date}
            </span>
          )}
        </div>
      )}

      {/* Summary */}
      {summary && (
        <div>
          <SectionHeading>National Security Impact Summary</SectionHeading>
          <SectionDivider />
          <p style={{ fontSize: 14, lineHeight: 1.6, margin: '12px 0 0', color: 'var(--gc-foreground, inherit)' }}>
            {summary}
          </p>
        </div>
      )}

      {/* Overall headline — Impact Profile radar (left) + rating (right),
          the same order as the harm-severity panel in the standard
          incident modal, so a reader moving between the two views finds
          the chart in the same place. */}
      <div style={{
        display: 'grid',
        // Two columns when there is room for two 260px ones; stacked below that.
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: 24,
        padding: '14px 16px',
        background: 'var(--gc-surface-muted, #f8fafc)',
        border: '1px solid var(--gc-border, #e5e7eb)',
        borderRadius: 6,
        alignItems: 'start',
      }}>
        <div>
          {spiderUrl ? (
            <div style={{ textAlign: 'center' }}>
              <img
                src={spiderUrl}
                alt="National Security Impact Profile (spider chart)"
                style={{ maxWidth: '100%', maxHeight: 320, borderRadius: 4 }}
              />
            </div>
          ) : showRadar ? (
            <NatSecRadar categories={radarCategories} />
          ) : (
            <div style={{ fontSize: 12, color: 'var(--gc-text-muted, #9ca3af)' }}>—</div>
          )}
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gc-text-muted, #6b7280)', marginBottom: 4 }}>
            Overall National Security Impact Score
          </div>
          <div style={{ fontSize: 11, color: 'var(--gc-text-muted, #9ca3af)', marginBottom: 10 }}>
            Based on the highest individual category rating
          </div>
          {/* Same size as the per-category pills in the table below —
              a larger hero pill read as a different kind of value. */}
          <ImpactPill score={overallScore} />
        </div>
      </div>

      {/* Affected Nations / Actors — two columns */}
      <div>
        <SectionHeading>Affected Nations / Actors</SectionHeading>
        <SectionDivider />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 24, marginTop: 4 }}>
          <div>
            <LabelValue label="Scope of Impact" value={scope} />
            <LabelValue label="Alleged Perpetrator" value={allegedPerpetrator} />
          </div>
          <div>
            <LabelValue label="Primary Target" value={primaryTarget} />
            <LabelValue label="Other Affected Actors" value={otherAffected} />
          </div>
        </div>
      </div>

      {/* Threat Characteristics */}
      <div>
        <SectionHeading>Threat Characteristics</SectionHeading>
        <SectionDivider />
        <div style={{ marginTop: 4 }}>
          <PillRow
            label="Imminence Level"
            pill={<ColouredPill label={imminenceLevel} bg={THREAT_PILL_COLOURS.imminence} />}
            justification={imminenceJustification}
          />
          <PillRow
            label="Autonomy Level"
            pill={<ColouredPill label={autonomyLevel} bg={THREAT_PILL_COLOURS.autonomy} />}
            justification={autonomyJustification}
          />
          <PillRow
            label="Novelty Level"
            pill={<ColouredPill label={noveltyLevel} bg={THREAT_PILL_COLOURS.novelty} />}
            justification={noveltyJustification}
          />
        </div>
      </div>

      {/* Per-category Impact assessment */}
      <div>
        <SectionHeading>National Security Impact</SectionHeading>
        <SectionDivider />
        <div style={{ fontSize: 12, color: 'var(--gc-text-muted, #6b7280)', margin: '10px 0 4px' }}>
          Scale: Negligible · Minor · Substantial · Severe · Critical
        </div>
        {IMPACT_CATEGORIES.map((cat) => (
          <PillRow
            key={cat.scoreKey}
            label={cat.label}
            pill={<ImpactPill score={readNumber(incident, cat.scoreKey)} />}
            justification={readString(incident, cat.justificationKey)}
          />
        ))}
      </div>
    </div>
  );
}

export function NatSecIncidentDetailModal({
  isOpen,
  onClose,
  incidentId,
  incident,
  loading,
}: NatSecIncidentDetailModalProps) {
  const title = readString(incident, 'title');

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="min(1000px, 92vw)">
      {loading ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--gc-text-muted, #6b7280)', fontSize: 14 }}>
          Loading National Security assessment…
        </div>
      ) : !incident ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--gc-text-muted, #6b7280)', fontSize: 14 }}>
          No incident data available.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* Header — matches the standard IncidentDetailModal pattern */}
          <div>
            <div style={{ fontSize: 12, color: 'var(--gc-text-muted, #6b7280)', marginBottom: 4 }}>
              Incident {incidentId}
            </div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, lineHeight: 1.3, color: 'var(--gc-foreground, inherit)' }}>
              {title || incidentId}
            </h2>
          </div>
          <NatSecDetailContent incidentId={incidentId} incident={incident} />
        </div>
      )}
    </Modal>
  );
}
